import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Typography,
  InputAdornment,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const JournalEntryForm = ({
  open,
  onClose,
  onSubmit,
  entry,
  isEditing,
  loading,
  validationErrors,
  onFieldChange
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          backgroundColor: 'var(--ntb-bg-secondary)',
          borderRadius: isMobile ? 0 : '12px',
          boxShadow: isMobile ? 'none' : '0 8px 32px var(--ntb-shadow-color)',
          margin: isMobile ? 0 : undefined,
          maxHeight: isMobile ? '100%' : '90vh',
          '& .MuiDialogTitle-root': {
            padding: isMobile ? '12px 16px' : '24px',
            borderBottom: '1px solid var(--ntb-border-color)'
          },
          '& .MuiDialogContent-root': {
            padding: isMobile ? '16px' : '24px',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch'
          },
          '& .MuiDialogActions-root': {
            padding: isMobile ? '12px 16px' : '16px 24px',
            borderTop: '1px solid var(--ntb-border-color)'
          }
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        m: 0,
        p: isMobile ? 1.5 : 2
      }}>
        <Typography variant="h5" sx={{
          color: 'var(--ntb-text-primary)',
          fontWeight: 600,
          fontSize: isMobile ? '1.1rem' : '1.5rem'
        }}>
          {isEditing ? 'Edit Entry' : 'New Entry'}
        </Typography>
        <IconButton 
          onClick={onClose}
          sx={{ 
            color: 'var(--ntb-text-tertiary)',
            '&:hover': { 
              backgroundColor: 'var(--ntb-hover-bg)',
              color: 'var(--ntb-text-primary)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={isMobile ? 2 : 3} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Trade Title"
              placeholder="Enter a descriptive title for your trade"
              fullWidth
              value={entry.title}
              onChange={(e) => onFieldChange('title', e.target.value)}
              error={!!validationErrors.title}
              helperText={validationErrors.title}
              required
              size={isMobile ? 'small' : 'medium'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'var(--ntb-bg-tertiary)',
                  borderRadius: '8px',
                  '& fieldset': {
                    borderColor: validationErrors.title ? 'var(--ntb-error-color)' : 'var(--ntb-border-color)',
                    transition: 'border-color 0.2s ease-in-out'
                  },
                  '&:hover fieldset': {
                    borderColor: validationErrors.title ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: validationErrors.title ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputLabel-root': {
                  color: validationErrors.title ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)',
                  fontSize: isMobile ? '0.85rem' : undefined,
                  '&.Mui-focused': {
                    color: validationErrors.title ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)'
                  }
                },
                '& .MuiInputBase-input': {
                  color: 'var(--ntb-text-primary)',
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  padding: isMobile ? '10px 12px' : '14px 16px'
                },
                '& .MuiFormHelperText-root': {
                  color: 'var(--ntb-error-color)'
                }
              }}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="Entry Price"
              type="number"
              placeholder="Entry price"
              fullWidth
              value={entry.entry_price}
              onChange={(e) => onFieldChange('entry_price', parseFloat(e.target.value))}
              error={!!validationErrors.entry_price}
              helperText={validationErrors.entry_price}
              size={isMobile ? 'small' : 'medium'}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'var(--ntb-bg-tertiary)',
                  borderRadius: '8px',
                  height: isMobile ? '40px' : '48px',
                  '& fieldset': {
                    borderColor: validationErrors.entry_price ? 'var(--ntb-error-color)' : 'var(--ntb-border-color)',
                    transition: 'border-color 0.2s ease-in-out'
                  },
                  '&:hover fieldset': {
                    borderColor: validationErrors.entry_price ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: validationErrors.entry_price ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputLabel-root': {
                  color: validationErrors.entry_price ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)',
                  fontSize: isMobile ? '0.8rem' : undefined,
                  '&.Mui-focused': {
                    color: validationErrors.entry_price ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)'
                  }
                },
                '& .MuiInputBase-input': {
                  color: 'var(--ntb-text-primary)',
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  padding: isMobile ? '8px 10px' : '12px 14px'
                },
                '& .MuiInputAdornment-root': {
                  color: 'var(--ntb-text-tertiary)',
                  '& .MuiTypography-root': { fontSize: isMobile ? '0.8rem' : undefined }
                },
                '& .MuiFormHelperText-root': {
                  color: 'var(--ntb-error-color)'
                }
              }}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="Exit Price"
              type="number"
              placeholder="Exit price"
              fullWidth
              value={entry.exit_price}
              onChange={(e) => onFieldChange('exit_price', parseFloat(e.target.value))}
              error={!!validationErrors.exit_price}
              helperText={validationErrors.exit_price}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'var(--ntb-bg-tertiary)',
                  borderRadius: '8px',
                  height: '48px',
                  '& fieldset': { 
                    borderColor: validationErrors.exit_price ? 'var(--ntb-error-color)' : 'var(--ntb-border-color)',
                    transition: 'border-color 0.2s ease-in-out'
                  },
                  '&:hover fieldset': { 
                    borderColor: validationErrors.exit_price ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: validationErrors.exit_price ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputLabel-root': { 
                  color: validationErrors.exit_price ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)',
                  '&.Mui-focused': {
                    color: validationErrors.exit_price ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)'
                  }
                },
                '& .MuiInputBase-input': { 
                  color: 'var(--ntb-text-primary)',
                  fontSize: '0.9rem',
                  padding: '12px 14px'
                },
                '& .MuiInputAdornment-root': {
                  color: 'var(--ntb-text-tertiary)'
                },
                '& .MuiFormHelperText-root': {
                  color: 'var(--ntb-error-color)'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Profit/Loss"
              type="number"
              placeholder="P/L amount"
              fullWidth
              value={entry.profit_loss}
              onChange={(e) => onFieldChange('profit_loss', parseFloat(e.target.value))}
              error={!!validationErrors.profit_loss}
              helperText={validationErrors.profit_loss}
              required
              inputProps={{ 
                step: "0.01",
                min: "-999999999",
                max: "999999999"
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'var(--ntb-bg-tertiary)',
                  borderRadius: '8px',
                  height: '48px',
                  '& fieldset': { 
                    borderColor: validationErrors.profit_loss ? 'var(--ntb-error-color)' : 'var(--ntb-border-color)',
                    transition: 'border-color 0.2s ease-in-out'
                  },
                  '&:hover fieldset': { 
                    borderColor: validationErrors.profit_loss ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: validationErrors.profit_loss ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputLabel-root': { 
                  color: validationErrors.profit_loss ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)',
                  '&.Mui-focused': {
                    color: validationErrors.profit_loss ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)'
                  }
                },
                '& .MuiInputBase-input': { 
                  color: 'var(--ntb-text-primary)',
                  fontSize: '0.9rem',
                  padding: '12px 14px'
                },
                '& .MuiInputAdornment-root': {
                  color: 'var(--ntb-text-tertiary)'
                },
                '& .MuiFormHelperText-root': {
                  color: 'var(--ntb-error-color)'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Return %"
              type="number"
              fullWidth
              value={entry.return_percentage || 0}
              onChange={(e) => onFieldChange('return_percentage', parseFloat(e.target.value) || 0)}
              error={!!validationErrors.return_percentage}
              helperText={validationErrors.return_percentage}
              inputProps={{ 
                step: "0.01",
                min: "-999999999",
                max: "999999999"
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'var(--ntb-bg-tertiary)',
                  borderRadius: '8px',
                  height: '48px',
                  '& fieldset': { 
                    borderColor: validationErrors.return_percentage ? 'var(--ntb-error-color)' : 'var(--ntb-border-color)',
                    transition: 'border-color 0.2s ease-in-out'
                  },
                  '&:hover fieldset': { 
                    borderColor: validationErrors.return_percentage ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: validationErrors.return_percentage ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputLabel-root': { 
                  color: validationErrors.return_percentage ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)',
                  '&.Mui-focused': {
                    color: validationErrors.return_percentage ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)'
                  }
                },
                '& .MuiInputBase-input': { 
                  color: 'var(--ntb-text-primary)',
                  fontSize: '0.9rem',
                  padding: '12px 14px'
                },
                '& .MuiFormHelperText-root': {
                  color: 'var(--ntb-error-color)'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Symbol"
              placeholder="e.g., AAPL, BTC/USD"
              fullWidth
              value={entry.instrument}
              onChange={(e) => onFieldChange('instrument', e.target.value)}
              error={!!validationErrors.instrument}
              helperText={validationErrors.instrument}
              required
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'var(--ntb-bg-tertiary)',
                  borderRadius: '8px',
                  height: '48px',
                  '& fieldset': { 
                    borderColor: validationErrors.instrument ? 'var(--ntb-error-color)' : 'var(--ntb-border-color)',
                    transition: 'border-color 0.2s ease-in-out'
                  },
                  '&:hover fieldset': { 
                    borderColor: validationErrors.instrument ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: validationErrors.instrument ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputLabel-root': { 
                  color: validationErrors.instrument ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)',
                  '&.Mui-focused': {
                    color: validationErrors.instrument ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)'
                  }
                },
                '& .MuiInputBase-input': { 
                  color: 'var(--ntb-text-primary)',
                  fontSize: '0.9rem',
                  padding: '12px 14px'
                },
                '& .MuiFormHelperText-root': {
                  color: 'var(--ntb-error-color)'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!validationErrors.trade_type}>
              <InputLabel sx={{ 
                color: validationErrors.trade_type ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)',
                '&.Mui-focused': {
                  color: validationErrors.trade_type ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)'
                }
              }}>
                Side
              </InputLabel>
              <Select
                value={entry.trade_type}
                label="Side"
                onChange={(e) => onFieldChange('trade_type', e.target.value)}
                required
                sx={{ 
                  backgroundColor: 'var(--ntb-bg-tertiary)',
                  borderRadius: '8px',
                  height: '48px',
                  '& .MuiOutlinedInput-notchedOutline': { 
                    borderColor: validationErrors.trade_type ? 'var(--ntb-error-color)' : 'var(--ntb-border-color)',
                    transition: 'border-color 0.2s ease-in-out'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': { 
                    borderColor: validationErrors.trade_type ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                    borderColor: validationErrors.trade_type ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)',
                    borderWidth: '2px'
                  },
                  '& .MuiSelect-select': {
                    color: 'var(--ntb-text-primary)',
                    fontSize: '0.9rem',
                    padding: '12px 14px'
                  }
                }}
              >
                <MenuItem value="LONG">LONG</MenuItem>
                <MenuItem value="SHORT">SHORT</MenuItem>
              </Select>
              {validationErrors.trade_type && (
                <Typography variant="caption" sx={{ color: 'var(--ntb-error-color)', mt: 1, ml: 2 }}>
                  {validationErrors.trade_type}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Notes"
              placeholder="Add your trade notes here..."
              fullWidth
              multiline
              rows={4}
              value={entry.content}
              onChange={(e) => onFieldChange('content', e.target.value)}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'var(--ntb-bg-tertiary)',
                  borderRadius: '8px',
                  '& fieldset': { 
                    borderColor: 'var(--ntb-border-color)',
                    transition: 'border-color 0.2s ease-in-out'
                  },
                  '&:hover fieldset': { 
                    borderColor: 'var(--ntb-text-tertiary)'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: 'var(--ntb-accent-primary)',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputLabel-root': { 
                  color: 'var(--ntb-text-tertiary)',
                  '&.Mui-focused': {
                    color: 'var(--ntb-accent-primary)'
                  }
                },
                '& .MuiInputBase-input': { 
                  color: 'var(--ntb-text-primary)',
                  fontSize: '0.9rem',
                  lineHeight: '1.5'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Trade Date & Time"
              type="datetime-local"
              fullWidth
              value={(() => {
                const date = entry.trade_date ? new Date(entry.trade_date) : new Date();
                // Adjust for local timezone
                const tzOffset = date.getTimezoneOffset() * 60000; // offset in milliseconds
                return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
              })()}
              onChange={(e) => onFieldChange('trade_date', e.target.value)}
              error={!!validationErrors.trade_date}
              helperText={validationErrors.trade_date}
              required
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'var(--ntb-bg-tertiary)',
                  borderRadius: '8px',
                  height: '48px',
                  '& fieldset': { 
                    borderColor: validationErrors.trade_date ? 'var(--ntb-error-color)' : 'var(--ntb-border-color)',
                    transition: 'border-color 0.2s ease-in-out'
                  },
                  '&:hover fieldset': { 
                    borderColor: validationErrors.trade_date ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: validationErrors.trade_date ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputLabel-root': { 
                  color: validationErrors.trade_date ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)',
                  '&.Mui-focused': {
                    color: validationErrors.trade_date ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)'
                  }
                },
                '& .MuiInputBase-input': { 
                  color: 'var(--ntb-text-primary)',
                  fontSize: '0.9rem',
                  padding: '12px 14px'
                },
                '& .MuiFormHelperText-root': {
                  color: 'var(--ntb-error-color)'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Entry Size"
              type="number"
              placeholder="Position size"
              fullWidth
              value={entry.entry_size || ''}
              onChange={(e) => onFieldChange('entry_size', e.target.value)}
              error={!!validationErrors.entry_size}
              helperText={validationErrors.entry_size}
              inputProps={{ step: "0.01" }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'var(--ntb-bg-tertiary)',
                  borderRadius: '8px',
                  height: '48px',
                  '& fieldset': { 
                    borderColor: validationErrors.entry_size ? 'var(--ntb-error-color)' : 'var(--ntb-border-color)',
                    transition: 'border-color 0.2s ease-in-out'
                  },
                  '&:hover fieldset': { 
                    borderColor: validationErrors.entry_size ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: validationErrors.entry_size ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputLabel-root': { 
                  color: validationErrors.entry_size ? 'var(--ntb-error-color)' : 'var(--ntb-text-tertiary)',
                  '&.Mui-focused': {
                    color: validationErrors.entry_size ? 'var(--ntb-error-color)' : 'var(--ntb-accent-primary)'
                  }
                },
                '& .MuiInputBase-input': { 
                  color: 'var(--ntb-text-primary)',
                  fontSize: '0.9rem',
                  padding: '12px 14px'
                },
                '& .MuiFormHelperText-root': {
                  color: 'var(--ntb-error-color)'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ 
                color: 'var(--ntb-text-tertiary)',
                '&.Mui-focused': {
                  color: 'var(--ntb-accent-primary)'
                }
              }}>
                Mood
              </InputLabel>
              <Select
                value={entry.mood || ''}
                label="Mood"
                onChange={(e) => onFieldChange('mood', e.target.value)}
                sx={{ 
                  backgroundColor: 'var(--ntb-bg-tertiary)',
                  borderRadius: '8px',
                  height: '48px',
                  '& .MuiOutlinedInput-notchedOutline': { 
                    borderColor: 'var(--ntb-border-color)',
                    transition: 'border-color 0.2s ease-in-out'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': { 
                    borderColor: 'var(--ntb-text-tertiary)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                    borderColor: 'var(--ntb-accent-primary)',
                    borderWidth: '2px'
                  },
                  '& .MuiSelect-select': {
                    color: 'var(--ntb-text-primary)',
                    fontSize: '0.9rem',
                    padding: '12px 14px'
                  }
                }}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="Confident">Confident</MenuItem>
                <MenuItem value="Nervous">Nervous</MenuItem>
                <MenuItem value="Frustrated">Frustrated</MenuItem>
                <MenuItem value="Excited">Excited</MenuItem>
                <MenuItem value="Calm">Calm</MenuItem>
                <MenuItem value="Anxious">Anxious</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Lessons Learned"
              placeholder="What did you learn from this trade?"
              fullWidth
              multiline
              rows={3}
              value={entry.lessons || ''}
              onChange={(e) => onFieldChange('lessons', e.target.value)}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'var(--ntb-bg-tertiary)',
                  borderRadius: '8px',
                  '& fieldset': { 
                    borderColor: 'var(--ntb-border-color)',
                    transition: 'border-color 0.2s ease-in-out'
                  },
                  '&:hover fieldset': { 
                    borderColor: 'var(--ntb-text-tertiary)'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: 'var(--ntb-accent-primary)',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputLabel-root': { 
                  color: 'var(--ntb-text-tertiary)',
                  '&.Mui-focused': {
                    color: 'var(--ntb-accent-primary)'
                  }
                },
                '& .MuiInputBase-input': { 
                  color: 'var(--ntb-text-primary)',
                  fontSize: '0.9rem',
                  lineHeight: '1.5'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ 
                color: 'var(--ntb-text-tertiary)',
                '&.Mui-focused': {
                  color: 'var(--ntb-accent-primary)'
                }
              }}>
                Method of Analysis
              </InputLabel>
              <Select
                value={entry.method_analytics || 'Manual'}
                label="Method of Analysis"
                onChange={(e) => onFieldChange('method_analytics', e.target.value)}
                sx={{ 
                  backgroundColor: 'var(--ntb-bg-tertiary)',
                  borderRadius: '8px',
                  height: '48px',
                  '& .MuiOutlinedInput-notchedOutline': { 
                    borderColor: 'var(--ntb-border-color)',
                    transition: 'border-color 0.2s ease-in-out'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': { 
                    borderColor: 'var(--ntb-text-tertiary)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                    borderColor: 'var(--ntb-accent-primary)',
                    borderWidth: '2px'
                  },
                  '& .MuiSelect-select': {
                    color: 'var(--ntb-text-primary)',
                    fontSize: '0.9rem',
                    padding: '12px 14px'
                  }
                }}
              >
                <MenuItem value="Manual">Manual</MenuItem>
                <MenuItem value="Copilot">Copilot</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose}
          sx={{ 
            color: 'var(--ntb-text-tertiary)',
            '&:hover': { 
              backgroundColor: 'var(--ntb-hover-bg)',
              color: 'var(--ntb-text-primary)'
            },
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.95rem'
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={loading}
          sx={{ 
            backgroundColor: 'var(--ntb-accent-primary)',
            '&:hover': { 
              backgroundColor: 'var(--ntb-accent-secondary)'
            },
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.95rem',
            px: 4,
            py: 1,
            borderRadius: '6px'
          }}
        >
          {loading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Entry')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JournalEntryForm; 